import { useCallback, useRef } from "react";
import { useSearchParams } from "react-router";
import {
  AnnotationFilter,
  ReviewFilter,
  TaskFilterState,
  TaskSortState,
} from "../components/TaskSortFilterDialog";

const TASK_PARAM = "task";
const ANNOTATED_PARAM = "annotated";
const LABELS_PARAM = "labels";
const PAGE_PARAM = "page";
const SORT_BY_PARAM = "sortBy";
const SORT_ORDER_PARAM = "sortOrder";
const UPDATED_BY_PARAM = "updatedBy";
const AWAITING_REVIEW_PARAM = "awaitingReview";

export const DEFAULT_SORT: TaskSortState = { sortBy: "createdAt", sortOrder: "desc" };

export type PageAnchor = "first" | "last";

const parseAnnotationFilter = (raw: string | null): AnnotationFilter =>
  raw === "true" || raw === "false" ? raw : "all";

/**
 * Orthogonal to `annotated`, not a third value of it: a task awaiting review
 * holds no real annotations, so it is TODO and matches `annotated=false` too.
 */
const parseReviewFilter = (raw: string | null): ReviewFilter =>
  raw === "true" || raw === "false" ? raw : "all";

const parseLabelIds = (raw: string | null): number[] => {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => Number.parseInt(s, 10))
    .filter((n) => Number.isFinite(n));
};

const parseUpdatedBy = (raw: string | null): number[] => {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => Number.parseInt(s, 10))
    .filter((n) => Number.isFinite(n));
};

const parsePage = (raw: string | null): number => {
  const n = Number.parseInt(raw ?? "", 10);
  return Number.isFinite(n) && n >= 1 ? n : 1;
};

const parseTaskId = (raw: string | null): number | null => {
  if (raw === null) return null;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) ? n : null;
};

const VALID_SORT_KEYS = [
  "createdAt",
  "updatedAt",
  "meanConfidence",
  "meanIou",
  "precision",
  "recall",
] as const;

const parseSortBy = (raw: string | null): TaskSortState["sortBy"] =>
  (VALID_SORT_KEYS as readonly string[]).includes(raw ?? "")
    ? (raw as TaskSortState["sortBy"])
    : "createdAt";

const parseSortOrder = (raw: string | null): "asc" | "desc" =>
  raw === "asc" ? "asc" : "desc";

const applyFilter = (params: URLSearchParams, filter: TaskFilterState) => {
  if (filter.annotationFilter === "all") params.delete(ANNOTATED_PARAM);
  else params.set(ANNOTATED_PARAM, filter.annotationFilter);
  if (filter.labelIds.length === 0) params.delete(LABELS_PARAM);
  else params.set(LABELS_PARAM, filter.labelIds.join(","));
  if (filter.updatedBy.length === 0) params.delete(UPDATED_BY_PARAM);
  else params.set(UPDATED_BY_PARAM, filter.updatedBy.join(","));
  if (filter.reviewFilter === "all") params.delete(AWAITING_REVIEW_PARAM);
  else params.set(AWAITING_REVIEW_PARAM, filter.reviewFilter);
};

const applySort = (params: URLSearchParams, sort: TaskSortState) => {
  if (sort.sortBy === DEFAULT_SORT.sortBy) params.delete(SORT_BY_PARAM);
  else params.set(SORT_BY_PARAM, sort.sortBy);
  if (sort.sortOrder === DEFAULT_SORT.sortOrder) params.delete(SORT_ORDER_PARAM);
  else params.set(SORT_ORDER_PARAM, sort.sortOrder);
};

/**
 * Single source of truth for the label page's URL state: `task`, `page`,
 * `annotated`, `labels`, `updatedBy`, `awaitingReview`, `sortBy`, `sortOrder`
 * query params.
 * One `useSearchParams` call means every setter shares the same closure,
 * so the updaters compose predictably across renders.
 */
export const useLabelUrlState = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const pendingAnchorRef = useRef<PageAnchor | null>(null);

  const selectedTaskId = parseTaskId(searchParams.get(TASK_PARAM));
  const page = parsePage(searchParams.get(PAGE_PARAM));
  const filter: TaskFilterState = {
    annotationFilter: parseAnnotationFilter(searchParams.get(ANNOTATED_PARAM)),
    labelIds: parseLabelIds(searchParams.get(LABELS_PARAM)),
    updatedBy: parseUpdatedBy(searchParams.get(UPDATED_BY_PARAM)),
    reviewFilter: parseReviewFilter(searchParams.get(AWAITING_REVIEW_PARAM)),
  };
  const sort: TaskSortState = {
    sortBy: parseSortBy(searchParams.get(SORT_BY_PARAM)),
    sortOrder: parseSortOrder(searchParams.get(SORT_ORDER_PARAM)),
  };

  const setSelectedTaskId = useCallback(
    (id: number, opts?: { replace?: boolean }) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set(TASK_PARAM, String(id));
          return next;
        },
        { replace: opts?.replace ?? false },
      );
    },
    [setSearchParams],
  );

  const setPage = useCallback(
    (nextPage: number, anchor: PageAnchor = "first") => {
      pendingAnchorRef.current = anchor;
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (nextPage <= 1) next.delete(PAGE_PARAM);
        else next.set(PAGE_PARAM, String(nextPage));
        next.delete(TASK_PARAM);
        return next;
      });
    },
    [setSearchParams],
  );

  const setSort = useCallback(
    (nextSort: TaskSortState) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        applySort(next, nextSort);
        next.delete(PAGE_PARAM);
        return next;
      });
    },
    [setSearchParams],
  );

  // The sort & filter modal commits both halves at once. `setSearchParams` reads
  // the params from its own render's closure, so two back-to-back calls would
  // make the second one clobber the first — hence a single writer for both.
  const setSortAndFilter = useCallback(
    (next: { sort: TaskSortState; filter: TaskFilterState }) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        applyFilter(params, next.filter);
        applySort(params, next.sort);
        params.delete(PAGE_PARAM);
        return params;
      });
    },
    [setSearchParams],
  );

  return {
    selectedTaskId,
    setSelectedTaskId,
    page,
    setPage,
    filter,
    sort,
    setSort,
    setSortAndFilter,
    pendingAnchorRef,
  };
};
